import { ReactElement } from 'react';
import { useTranslation } from 'next-i18next';

import Button from '@Hotels-at-Home/hah-fe-core/dist/components/Button';
import { sanitizeHtml } from '@Hotels-at-Home/hah-fe-core/dist/utils';
import TitleFlip from '../../helpers/TitleFlip';

const BeddingSection = (): ReactElement => {
  const { t } = useTranslation('', { keyPrefix: '$pages.$home.$beddingSection' });
  const { t: tButton } = useTranslation('', { keyPrefix: '$common.$buttons' });
  const { t: tCommon } = useTranslation('', { keyPrefix: '$common' });
  const { t: tLink } = useTranslation('', { keyPrefix: '$local.$homePageLinks.$bedding' });

  return (
    <section className="base-section bedding-section bedding">
      <div className="branding-hero desktop-viewport">
        <p
          className="brand-copy txt"
          data-gsap="fade-up"
          data-gsap-delay="0.5"
          data-gsap-above-fold-stop
        >
          <span>{t('thoughtful')}</span>
        </p>
        <div className="img-wrap-tall">
          <a href={tLink('bedding')} aria-label={t('bedding')}>
            <picture>
              <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
              <source srcSet="/images/homePage/beddingSection/bedding-background.webp" />
              <img
                data-gsap="zoom-out"
                data-gsap-delay="0.03"
                data-gsap-above-fold-stop
                src="/images/homePage/beddingSection/bedding-background.webp"
                alt={t('hpBedding')}
                width={849}
                height={923}
                decoding="async"
              />
            </picture>
          </a>
          {/* <div className="cinema-gif">
            <picture>
              <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
              <img
                data-gsap="zoom-out"
                data-gsap-above-fold-stop
                src="/images/homePage/beddingSection/bedding-overlay.gif"
                alt={t('hpBedding')}
                width={849}
                height={923}
                loading="lazy"
              />
            </picture>
          </div> */}
        </div>
      </div>
      <div className="outer-wrap desktop-viewport">
        <div className="inner-wrap" data-gsap="fade" data-gsap-above-fold-stop>
          <div className="title">
            <h2 data-gsap="fade-up" className="section-title" data-gsap-above-fold-stop>
              <TitleFlip normalText={t('marriottHotels')}>
                <span className="accent-bar">
                  {t('bedding')}
                  <em data-gsap="grow" data-gsap-delay="0.4 " />
                </span>
              </TitleFlip>
            </h2>
          </div>

          <div className="bedding-products">
            <a
              className="bedding-product-wrap hover-btn-animation"
              href={tLink('beddingSets')}
              id="HP_bedding_bedding-set"
            >
              <picture>
                <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                <source srcSet="/images/homePage/beddingSection/hp-bedding-sets.webp" />
                <img
                  data-gsap="fade"
                  data-gsap-delay="0.4"
                  data-gsap-above-fold-stop
                  src="/images/homePage/beddingSection/hp-bedding-sets.webp"
                  alt={t('marriottBeddingSets')}
                  width={755}
                  height={787}
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                />
              </picture>
              <h3 className="section-subtitle">
                <span data-gsap="fade-left" data-gsap-bottom="0" data-gsap-above-fold-stop>
                  {t('beddingSets')}
                </span>
              </h3>
              <div className="hover-content">
                <Button
                  className="base-btn bg-red"
                  noTabbing
                  ariaLabel={`${tButton('shopNow')}: ${t('beddingSets')}`}
                >
                  {tButton('shopNow')}
                </Button>
              </div>
            </a>
            <a
              className="bedding-product-wrap hover-btn-animation"
              href={tLink('duvets')}
              id="HP_bedding_duvets"
            >
              <picture>
                <source srcSet="/images/homePage/fill.gif" media="(max-width: 800px)" />
                <source srcSet="/images/homePage/beddingSection/hp-duvets.webp" />
                <img
                  data-gsap="fade"
                  data-gsap-delay="0.4"
                  data-gsap-above-fold-stop
                  src="/images/homePage/beddingSection/hp-duvets.webp"
                  alt={tCommon('productName', { productName: t('duvets') })}
                  width={755}
                  height={787}
                  loading="lazy"
                  decoding="async"
                  fetchPriority="low"
                />
              </picture>
              <h3 className="section-subtitle">
                <span data-gsap="fade-left" data-gsap-bottom="0" data-gsap-above-fold-stop>
                  {t('duvets')}
                </span>
              </h3>
              <div className="hover-content">
                <Button
                  className="base-btn bg-red"
                  noTabbing
                  ariaLabel={`${tButton('shopNow')}: ${t('duvets')}`}
                >
                  {tButton('shopNow')}
                </Button>
              </div>
            </a>
          </div>
        </div>
      </div>
      <div className="hp-mobile-viewport mob-bedding">
        <h2 className="mob-title">
          <TitleFlip normalText={t('marriott')}>
            <span className="accent-bar">
              {t('bedding')}
              <em data-gsap="grow" data-gsap-delay="0.6" data-gsap-duration="0.8" />
            </span>
          </TitleFlip>
        </h2>
        <a className="mob-main-section" href={tLink('bedding')} id="HP_bedding_mob">
          <picture>
            <source srcSet="/images/homePage/fill.gif" media="(min-width: 801px)" />
            <source srcSet="/images/homePage/beddingSection/hp-bedding-mob.webp" />
            <img
              src="/images/homePage/beddingSection/hp-bedding-mob.webp"
              alt={t('bedding')}
              width={786}
              height={419}
              loading="eager"
              fetchPriority="high"
            />
          </picture>
        </a>
        <div className="shop-list">
          <a className="shop-list-item" href={tLink('beddingSets')} id="CTA-BeddingSets-Mob">
            <picture>
              <source srcSet="/images/homePage/fill.gif" media="(min-width: 801px)" />
              <source srcSet="/images/homePage/beddingSection/marriott-signature-bed-and-bedding-set-MAR-101-02-WH_lrg.webp" />
              <img
                src="/images/homePage/beddingSection/marriott-signature-bed-and-bedding-set-MAR-101-02-WH_lrg.webp"
                alt={t('marriottBeddingSets')}
                width={1123}
                height={732}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
              />
            </picture>
            <h3
              className="mob-section mob-section-half-width txt"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('beddingSets')) }}
            />
          </a>
          <a className="shop-list-item" href={tLink('duvets')} id="CTA-Duvets-Mob">
            <picture>
              <source srcSet="/images/homePage/fill.gif" media="(min-width: 801px)" />
              <source srcSet="/images/homePage/beddingSection/Marriott-blankets-duvets_lrg.webp" />
              <img
                src="/images/homePage/beddingSection/Marriott-blankets-duvets_lrg.webp"
                alt={tCommon('productName', { productName: t('duvets') })}
                width={1123}
                height={732}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
              />
            </picture>
            <h3
              className="mob-section mob-section-half-width txt"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('duvets')) }}
            />
          </a>
        </div>
      </div>
    </section>
  );
};

export default BeddingSection;
